import { HasSessionSuccessResponse } from '@schibsted/account-sdk-browser';
import type { Player } from '@schibsted-svp/web-player';
import { getGlimrKeywords, hasGlimrPermission } from '@vgtv/glimr-utils';

import { GlimrConfig } from './types';

const isSuccessfulResponse = (
  hasSessionResponse: HasSessionSuccessResponse
): hasSessionResponse is HasSessionSuccessResponse & { sdrn: string } => {
  return (
    (hasSessionResponse as HasSessionSuccessResponse & { sdrn: string })
      .sdrn !== undefined &&
    (hasSessionResponse as HasSessionSuccessResponse).sig !== undefined
  );
};

const isValidConfig = (
  config: GlimrConfig | undefined
): config is GlimrConfig => {
  return (
    config !== undefined &&
    config.enabled === true &&
    typeof config.clientId === 'string' &&
    config.clientId.length > 0 &&
    typeof config.keywordPrefix === 'string' &&
    config.keywordPrefix.length > 0 &&
    typeof config.country === 'string' &&
    config.keywordPrefix.length > 0
  );
};

export async function checkGlimrPermission({
  player,
  config,
}: {
  player?: Player;
  config?: GlimrConfig;
}) {
  if (!isValidConfig(config) || !player) {
    return false;
  }

  try {
    return await hasGlimrPermission({
      schibstedAdChoicesEnabled: config.schibstedAdChoicesEnabled,
    });
  } catch (error) {
    console.error('Error checking Glimr permission:', error);
    return false;
  }
}

let checkGlimrPermissionPromise: Promise<boolean> | null = null;

export const getGlimrPermissionOnce = ({
  player,
  config,
}: {
  player?: Player;
  config?: GlimrConfig;
}) => {
  if (!checkGlimrPermissionPromise) {
    checkGlimrPermissionPromise = checkGlimrPermission({ player, config });
  }
  return checkGlimrPermissionPromise;
};

export async function prepareGlimrKeywords({
  player,
  config,
}: {
  player?: Player;
  config?: GlimrConfig;
}) {
  if (!isValidConfig(config) || !player) {
    return {};
  }

  const hasPermission = await getGlimrPermissionOnce({ player, config });
  if (!hasPermission) {
    return {};
  }

  const keywords = await getGlimrKeywords({
    clientId: config.clientId,
    keywordPrefix: config.keywordPrefix,
  });

  if (keywords) {
    return Object.entries(keywords)
      .map(([k, v]) => [k, v.includes(',') ? v.split(',') : v])
      .reduce((aggr, [k, v]) => ({ ...aggr, [k as string]: v }), {});
  }

  return keywords;
}
